/**
 * Response Normalizer
 * Normaliserer alle response typer til 0..1 skala
 */

import { getResponseTypeConfig } from '../data/topics/index.js'

/**
 * Normaliser en response værdi til 0..1
 * @param {string} responseType - YNM, IMPORTANCE_0_3, FREQUENCY_0_4
 * @param {string|number} value - Den rå response værdi
 * @returns {number} Normaliseret værdi (0..1)
 */
export function normalizeResponse(responseType, value) {
  const config = getResponseTypeConfig(responseType)
  
  if (!config) {
    console.warn(`Unknown response type: ${responseType}, defaulting to 0`)
    return 0
  }
  
  const normalized = config.values[value]
  
  if (normalized === undefined) {
    console.warn(`Unknown value "${value}" for type "${responseType}", defaulting to 0`)
    return 0
  }
  
  return normalized
}

/**
 * Normaliser alle responses i et response object
 * @param {Object} responses - {itemId: value, ...}
 * @param {Object} items - Items med responseType info
 * @returns {Object} Normaliserede responses {itemId: 0..1, ...}
 */
export function normalizeAllResponses(responses, items) {
  const normalized = {}
  
  for (const [itemId, value] of Object.entries(responses)) {
    const item = items.find(i => i.id === itemId)
    
    if (!item) {
      console.warn(`Item ${itemId} not found`)
      continue
    }
    
    const responseType = item.responseType || item.defaults?.responseType || 'YNM'
    normalized[itemId] = normalizeResponse(responseType, value)
  }
  
  return normalized
}

/**
 * Beregn gennemsnitlig match score mellem to response sets
 * @param {Object} responses1 - Normaliserede responses fra person 1
 * @param {Object} responses2 - Normaliserede responses fra person 2
 * @returns {number} Match score (0..1)
 */
export function calculateMatchScore(responses1, responses2) {
  const commonItems = Object.keys(responses1).filter(id => responses2[id] !== undefined)
  
  if (commonItems.length === 0) {
    return 0
  }
  
  let totalDifference = 0
  
  for (const itemId of commonItems) {
    const diff = Math.abs(responses1[itemId] - responses2[itemId])
    totalDifference += diff
  }
  
  // Konverter difference til match score (0 = perfekt match, 1 = total mismatch)
  const avgDifference = totalDifference / commonItems.length
  const matchScore = 1 - avgDifference
  
  return Math.max(0, Math.min(1, matchScore))
}
